import { Injectable } from "@angular/core";
import { TranslocoService } from "@jsverse/transloco";
import { createEffect, ofType, Actions } from "@ngrx/effects";
import { createSelector, createAction, props, createReducer, on } from "@ngrx/store";
import { exhaustMap, map, catchError, of, take, mergeMap, tap } from "rxjs";
import { ITimeSupplement, ISearchTimeSupplementsRequest, ICreateTimeSupplementRequest, IUpdateTimeSupplementRequest } from "src/app/setting/time-supplement/time-supplement";
import { TimeSupplementService } from "src/app/setting/time-supplement/time-supplement.service";
import { AppStateInterface } from "../appState.interface";
import { AppNotificationService } from "src/app/shared/services/notification.service";
import { TimeSpan } from "src/app/shared/helpers/time-span";

/*** STATE ***/
export interface TimeSupplementsStateInterface {
  isLoading: boolean;
  timeSupplements: ITimeSupplement[];
  total: number,
  error: string | null;
}

export const initialTimeSupplementsState: TimeSupplementsStateInterface = {
  isLoading: false,
  timeSupplements: [],
  total: 0,
  error: null
};

/*** SELECTORS ***/
export const selectTimeSupplementsFeature = (state: AppStateInterface) => state.timeSupplements;

export const allTimeSupplementsSelector = createSelector(
  selectTimeSupplementsFeature,
  (state) => state.timeSupplements
);

export const timeSupplementsTotalSelector = createSelector(
    selectTimeSupplementsFeature, 
    (state) => state.total
);

export const timeSupplementsLoadingSelector = createSelector(
  selectTimeSupplementsFeature,
  (state) => state.isLoading
);

export const timeSupplementsErrorSelector = createSelector(
  selectTimeSupplementsFeature,
  (state) => state.error
);

/*** ACTIONS ***/
// Initial load (one-time “get” similar to your getBkVats)
export const getTimeSupplements = createAction('[TimeSupplements] Get');
export const getTimeSupplementsSuccess = createAction(
  '[TimeSupplements] Get Success',
  props<{ timeSupplements: ITimeSupplement[], total: number }>()
);
export const getTimeSupplementsFailure = createAction(
  '[TimeSupplements] Get Failure',
  props<{ error: string }>()
);

// Search (with request payload)
export const searchTimeSupplements = createAction(
  '[TimeSupplements] Search',
  props<{ request: ISearchTimeSupplementsRequest }>()
);
export const searchTimeSupplementsSuccess = createAction(
  '[TimeSupplements] Search Success',
  props<{ timeSupplements: ITimeSupplement[], total: number }>()
);
export const searchTimeSupplementsFailure = createAction(
  '[TimeSupplements] Search Failure',
  props<{ error: string }>()
);

// Create
export const createTimeSupplement = createAction(
  '[TimeSupplements] Create',
  props<{ request: ICreateTimeSupplementRequest }>()
);
export const createTimeSupplementSuccess = createAction(
  '[TimeSupplements] Create Success',
  props<{ timeSupplement: ITimeSupplement }>()
);
export const createTimeSupplementFailure = createAction(
  '[TimeSupplements] Create Failure',
  props<{ error: string }>()
);

// Update
export const updateTimeSupplement = createAction(
  '[TimeSupplements] Update',
  props<{ request: IUpdateTimeSupplementRequest }>()
);
export const updateTimeSupplementSuccess = createAction(
  '[TimeSupplements] Update Success',
  props<{ timeSupplement: ITimeSupplement }>()
);
export const updateTimeSupplementFailure = createAction(
  '[TimeSupplements] Update Failure',
  props<{ error: string }>()
);

// Delete
export const deleteTimeSupplement = createAction(
  '[TimeSupplements] Delete',
  props<{ id: string }>()
);
export const deleteTimeSupplementSuccess = createAction(
  '[TimeSupplements] Delete Success',
  props<{ id: string }>()
);
export const deleteTimeSupplementFailure = createAction(
  '[TimeSupplements] Delete Failure',
  props<{ error: string }>()
);

/*** REDUCER ***/
export const timeSupplementsReducer = createReducer(
  initialTimeSupplementsState,

  // get (one-time boot)
  on(getTimeSupplements, (state) => ({ ...state, isLoading: true })),
  on(getTimeSupplementsSuccess, (state, action) => ({
    ...state,
    isLoading: false,
    timeSupplements: action.timeSupplements,
    total: action.total
  })),
  on(getTimeSupplementsFailure, (state, action) => ({
    ...state,
    isLoading: false,
    error: action.error
  })),

  // search
  on(searchTimeSupplements, (state) => ({ ...state, isLoading: true })),
  on(searchTimeSupplementsSuccess, (state, action) => ({
    ...state,
    isLoading: false,
    timeSupplements: action.timeSupplements,
    total: action.total
  })),
  on(searchTimeSupplementsFailure, (state, action) => ({
    ...state,
    isLoading: false,
    error: action.error
  })),

  // create
  on(createTimeSupplement, (state) => ({ ...state, isLoading: true })),
  on(createTimeSupplementSuccess, (state, action) => ({
    ...state,
    isLoading: false,
    total: state.total + 1,
    timeSupplements: [...state.timeSupplements, action.timeSupplement]
  })),
  on(createTimeSupplementFailure, (state, action) => ({
    ...state,
    isLoading: false,
    error: action.error
  })),

  // update
  on(updateTimeSupplement, (state) => ({ ...state, isLoading: true })),
  on(updateTimeSupplementSuccess, (state, action) => ({
    ...state,
    isLoading: false,
    timeSupplements: state.timeSupplements.map((ts) =>
      ts.id === action.timeSupplement.id ? action.timeSupplement : ts
    )
  })),
  on(updateTimeSupplementFailure, (state, action) => ({
    ...state,
    isLoading: false,
    error: action.error
  })),

  // delete
  on(deleteTimeSupplement, (state) => ({ ...state, isLoading: true })),
  on(deleteTimeSupplementSuccess, (state, action) => ({
    ...state,
    isLoading: false,
    total: state.total - 1,
    timeSupplements: state.timeSupplements.filter((ts) => ts.id !== action.id)
  })),
  on(deleteTimeSupplementFailure, (state, action) => ({
    ...state,
    isLoading: false,
    error: action.error
  }))
);

/*** EFFECTS ***/
@Injectable()
export class TimeSupplementsEffects {
  // One-time boot (same pattern as getBkVats$ with take(1))
  getTimeSupplements$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getTimeSupplements),
      exhaustMap(() => {
        // Hard-coded “boot” search like your example
        const request: ISearchTimeSupplementsRequest = {
          pageNumber: 1,
          pageSize: 999999,
          orderBy: ['timeFrom asc']
        };

        return this.timeSupplementService.searchTimeSupplements(request).pipe(
          map((data) =>
            getTimeSupplementsSuccess({
              // Robust extraction for different IPagedResults shapes
              timeSupplements: data.data.map((item) => this.convertStringToTimeSpan(item)),
              total: data.totalCount
            })
          ),
          catchError((error) =>
            of(getTimeSupplementsFailure({ error: error?.message ?? String(error) }))
          )
        );
      }),
      take(1) // make sure it runs once
    );
  });

  // List/Search with custom request
  searchTimeSupplements$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(searchTimeSupplements),
      mergeMap(({ request }) =>
        this.timeSupplementService.searchTimeSupplements(request).pipe(
          map((data: any) =>
            searchTimeSupplementsSuccess({
              timeSupplements: data.data.map((item) => this.convertStringToTimeSpan(item)),
              total: data.totalCount
            })
          ),
          catchError((error) =>
            of(searchTimeSupplementsFailure({ error: error?.message ?? String(error) }))
          )
        )
      )
    );
  });

  // Create
  createTimeSupplement$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(createTimeSupplement),
      mergeMap(({ request }) =>
        this.timeSupplementService.createTimeSupplement(request).pipe(
          // Some backends wrap results; your service returns ITimeSupplement directly.
          map((response) =>
            createTimeSupplementSuccess({
              timeSupplement: this.convertStringToTimeSpan(response)
            })
          ),
          tap(() => this.successNotification()),
          catchError((error) =>
            of(createTimeSupplementFailure({ error: error?.message ?? String(error) }))
          )
        )
      )
    );
  });

  // Update
  updateTimeSupplement$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(updateTimeSupplement),
      mergeMap(({ request }) =>
        this.timeSupplementService.updateTimeSupplement(request).pipe(
          map((response) =>
            updateTimeSupplementSuccess({
              timeSupplement: this.convertStringToTimeSpan(response)
            })
          ),
          tap(() => this.successNotification()),
          catchError((error) =>
            of(updateTimeSupplementFailure({ error: error?.message ?? String(error) }))
          )
        )
      )
    );
  });

  // Delete
  deleteTimeSupplement$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(deleteTimeSupplement),
      mergeMap(({ id }) =>
        this.timeSupplementService.deleteTimeSupplement(id).pipe(
          // If your API returns an envelope, you can filter(x => x.status === 'Ok') as in your example.
          map(() => deleteTimeSupplementSuccess({ id })),
          tap(() => this.successNotification('deleteSuccess')),
          catchError((error) =>
            of(deleteTimeSupplementFailure({ error: error?.message ?? String(error) }))
          )
        )
      )
    );
  });

  private successNotification(message: string = null) {
    this.notification.showNotification({
      message: this.transloco.translate(message ?? 'saveSuccess'),
      style: "success"
    });
  }

  private convertStringToTimeSpan(timeSupplement: ITimeSupplement): ITimeSupplement {
    return {
      ...timeSupplement,
      timeFromObj: TimeSpan.parse(timeSupplement.timeFrom),
      timeToObj: TimeSpan.parse(timeSupplement.timeTo)
    };
  }

  constructor(
    private actions$: Actions,
    private timeSupplementService: TimeSupplementService,
    private notification: AppNotificationService,
    private transloco: TranslocoService
  ) {}
}
